import { Row, Col, Table, Spin, Select, Input, Tag, Button, Modal, Descriptions, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { useMutate } from "hooks/useMutate";
import { getClientid, getUserInfo, getUserList } from "redux/selectors";
import { useSelector } from "react-redux";
import { useState } from "react";
import dayjs from "dayjs";
import { ButtonFilled } from "reusableComponent/UIButtons";

const STATUS_LABELS = {
  Requested: "Waiting for Approval",
  Approved: "Approved",
  Declined: "Declined",
};

const STATUS_COLORS = {
  Requested: "gold",
  Approved: "green",
  Declined: "red",
};

const MatterApproval = ({ pfaapprovalpermission }) => {
  const userInfo = useSelector(getUserInfo);
  const userClientId = useSelector(getClientid);
  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(undefined);

  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // 'approve' | 'decline'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getRequestedMatters, updateRequestedMatter } = queryKeys;
  const { mutateAsync: updateApprovalStatus } = useMutate(
    updateRequestedMatter.key,
    updateRequestedMatter.url
  );
  const queryParams = new URLSearchParams();
  queryParams.append("userClientId", userClientId);
  const queryString = queryParams.toString();
  const { key, url } = getRequestedMatters(queryString);

  const {
    data: requestedEngagementsRes,
    refetch,
    isLoading,
  } = useFetch(key, url, {
    enabled: !!userInfo?.userid,
  });

  const proposalData = requestedEngagementsRes?.response?.data?.length
    ? requestedEngagementsRes.response.data.map((item) => {
      const fileDoc = item?.requestedengagementdoc?.find(
        (doc) => doc?.fileName
      );
      return {
        engagementId: item.matter_id,
        matterId: item.matter_id || item.matterId || item.matterid || null,
        clientName: item.clientname || item.clientName || "---",
        matterName: item.matter_name,
        engagementType: item.engagement_category,
        startDate: item.start_date
          ? dayjs(item.start_date).format("Do MMM, YYYY")
          : "---",
        endDate: item.end_date
          ? dayjs(item.end_date).format("Do MMM, YYYY")
          : "---",
        fileName: fileDoc?.fileName || null,
        fileUrl: fileDoc?.publicurl || null,
        comments: item.comments || null,
        status: item.requestedstatus || "Requested",
      };
    })
    : [];

  const filteredData = proposalData.filter((item) => {
    const matchesSearch = searchText
      ? item.clientName?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.matterName?.toLowerCase().includes(searchText.toLowerCase())
      : true;
    const matchesStatus = selectedStatus
      ? item.status === selectedStatus
      : true;
    return matchesSearch && matchesStatus;
  });

  // Handlers
  const handleViewClick = (record) => {
    setSelectedRecord(record);
    setDetailModalOpen(true);
  };

  const handleDetailModalClose = () => {
    setDetailModalOpen(false);
    setSelectedRecord(null);
  };

  const handleApproveClick = () => {
    setPendingAction("approve");
    setDetailModalOpen(false);
    setConfirmModalOpen(true);
  };

  const handleDeclineClick = () => {
    setPendingAction("decline");
    setDetailModalOpen(false);
    setConfirmModalOpen(true);
  };

  const handleConfirmCancel = () => {
    setConfirmModalOpen(false);
    setDetailModalOpen(true);
    setPendingAction(null);
  };

  const handleConfirmAction = async () => {
    if (!selectedRecord?.engagementId) return;

    const requestedstatus = pendingAction === "approve" ? "Approved" : "Declined";

    setIsSubmitting(true);
    try {
      await updateApprovalStatus({
        matter_id: selectedRecord.matterId,
        requestedstatus,
        userClientId,
      });

      message.success(
        `Matter successfully ${requestedstatus.toLowerCase()}.`
      );
      setConfirmModalOpen(false);
      setPendingAction(null);
      setSelectedRecord(null);
      refetch();
    } catch (error) {
      console.error("Approval status update failed:", error);
      message.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isApprovedRecord = selectedRecord?.status === "Approved";
  const isDeclinedRecord = selectedRecord?.status === "Declined";
  const isDecidedRecord = isApprovedRecord || isDeclinedRecord;

  const renderDetailContent = (record) => (
    <Descriptions
      column={2}
      colon={false}
      labelStyle={{ color: "#8c8c8c", width: 160 }}
    >
      <Descriptions.Item label="Client" span={2}>
        {record?.clientName}
      </Descriptions.Item>
      <Descriptions.Item label="Matter Name" span={2}>
        {record?.matterName}
      </Descriptions.Item>
      <Descriptions.Item label="Start Date">
        {record?.startDate}
      </Descriptions.Item>
      <Descriptions.Item label="End Date">
        {record?.endDate}
      </Descriptions.Item>
      <Descriptions.Item label="Attached file" span={2}>
        {record?.fileName ? (
          <a href={record.fileUrl} target="_blank" rel="noopener noreferrer">
            {record.fileName}
          </a>
        ) : (
          "---"
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Comments" span={2}>
        {record?.comments ||
          "You are about to update the status of this proposal. If the client has already accepted or declined it outside the system, you can record that response here to keep everything up to date."}
      </Descriptions.Item>
    </Descriptions>
  );

  const columns = [
    {
      title: "Client",
      dataIndex: "clientName",
      key: "clientName",
      ellipsis: true,
    },
    {
      title: "Matter Name",
      dataIndex: "matterName",
      key: "matterName",
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
    },
    {
      title: "File Attached",
      dataIndex: "fileName",
      key: "fileName",
      render: (_, record) =>
        record.fileName ? (
          <a href={record.fileUrl} target="_blank" rel="noopener noreferrer">
            {record.fileName}
          </a>
        ) : (
          "---"
        ),
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      ellipsis: true,
      render: (text) => (text ? text : "---"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span>{STATUS_LABELS[status] || status}</span>
      ),
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      width: 100,
      align: "center",
      render: (_, record) => (
        <ButtonFilled
          size="small"
          onClick={() => handleViewClick(record)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          View
        </ButtonFilled>
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <span style={{ fontWeight: 600 }}>
            Total Proposal: {filteredData.length}
          </span>
        </Col>
        <Col>
          <Row gutter={12}>
            <Col>
              <Input
                placeholder="Search"
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col>
              <Select
                placeholder="Status"
                style={{ minWidth: 160 }}
                value={selectedStatus}
                onChange={setSelectedStatus}
                allowClear
              >
                <Select.Option value="Requested">
                  Waiting for Approval
                </Select.Option>
                <Select.Option value="Approved">Approved</Select.Option>
                <Select.Option value="Declined">Declined</Select.Option>
              </Select>
            </Col>
          </Row>
        </Col>
      </Row>

      <Spin spinning={isLoading}>
        <Table
          columns={columns}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "" : "non-white"
          }
          rowKey="engagementId"
          dataSource={filteredData}
          pagination={false}
        />
      </Spin>

      {/* Detail Modal */}
      <Modal
        title={
          isApprovedRecord
            ? "Matter Request - Approved"
            : isDeclinedRecord
              ? "Matter Request - Declined"
              : "Matter Request"
        }
        open={detailModalOpen}
        onCancel={handleDetailModalClose}
        footer={
          isDecidedRecord
            ? null
            : [
              <Button key="decline" onClick={handleDeclineClick}>
                Decline
              </Button>,
              <Button
                key="approve"
                style={{
                  background: "#00685F",
                  borderColor: "#00685F",
                  color: "#000",
                }}
                onClick={handleApproveClick}
              >
                Approve
              </Button>,
            ]
        }
        width={560}
      >
        {selectedRecord && renderDetailContent(selectedRecord)}
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        open={confirmModalOpen}
        onCancel={handleConfirmCancel}
        footer={null}
        width={420}
        closable={false}
        centered
      >
        <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
          {pendingAction === "approve" ? (
            <>
              <p style={{ marginBottom: 8 }}>
                Once approved, this request will be moved to engagement menu for
                your employees to proceed with engagement creation
              </p>
              <p style={{ marginBottom: 24 }}>
                Are you sure you want to approve this request?
              </p>
            </>
          ) : (
            <>
              <p style={{ marginBottom: 8 }}>
                This request will be marked as declined and the employee will be
                notified.
              </p>
              <p style={{ marginBottom: 24 }}>
                Are you sure you want to decline this request?
              </p>
            </>
          )}
          <Row justify="center" gutter={12}>
            <Col>
              <Button onClick={handleConfirmCancel} disabled={isSubmitting}>
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                style={{
                  background: pendingAction === "approve" ? "#00685F" : "#00685F",
                  borderColor: pendingAction === "approve" ? "#00685F" : "#00685F",
                  color: "#000",
                }}
                loading={isSubmitting}
                onClick={handleConfirmAction}
              >
                {pendingAction === "approve" ? "Approve" : "Decline"}
              </Button>
            </Col>
          </Row>
        </div>
      </Modal>
    </>
  );
};

export default MatterApproval;